import gsap from 'gsap';
import type { TransitionDirection } from '../types/transition.types';
import { type RefObject } from 'react';

function parseChild(
  child: gsap.core.Timeline | gsap.core.Tween,
  timeline: gsap.core.Timeline,
  direction: TransitionDirection,
): void {
  if ('getChildren' in child) {
    parseChildTimeline(child, timeline, direction);
  } else {
    parseChildTween(child as gsap.core.Tween, timeline, direction);
  }
}

function parseChildTimeline(
  child: gsap.core.Timeline,
  timeline: gsap.core.Timeline,
  direction: TransitionDirection,
): void {
  const subTimeline = gsap.timeline(child.vars);

  // Re-call the parse method for each of the children

  child.getChildren(false).forEach((subChild) => parseChild(subChild, subTimeline, direction));

  // Add the timeline to the parent timeline
  timeline.add(subTimeline.restart(), child.startTime());
}

function parseChildTween(
  child: gsap.core.Tween,
  timeline: gsap.core.Timeline,
  direction: TransitionDirection,
): void {
  if (direction === 'out' && child.vars.startAt) {
    // console.log(
    //   'Do not use `from` or `fromTo` when nesting transitionOutTimelines, use `to` instead!',
    // );
  }

  if (direction === 'in' && !child.vars.startAt) {
    // console.log('Do not use from while nesting transitionInTimelines, use fromTo instead!');
  }

  const { startAt: from, ...to } = child.vars;
  const targets = child.targets();
  const startTime = child.startTime();

  if (from) {
    // Detect if we have a `fromTo-scroll`, if so add a copy to the new timeline.
    timeline.fromTo(targets, from, to, startTime);
  } else if (child.vars.runBackwards) {
    timeline.from(targets, child.vars, startTime);
  } else {
    timeline.to(targets, child.vars, startTime);
  }
}

export function cloneTimeline(
  source: gsap.core.Timeline,
  direction: TransitionDirection,
): gsap.core.Timeline {
  const timeline = gsap.timeline(source.vars);

  source.getChildren(false).forEach((child) => parseChild(child, timeline, direction));

  return timeline;
}

export function clearTimeline(timeline: gsap.core.Timeline, isRoot: boolean = true): void {
  timeline.getChildren().forEach((child) => {
    if ('getChildren' in child) {
      clearTimeline(child, false);
    } else {
      gsap.set(child.targets(), { clearProps: 'all' });
    }
  });

  timeline.clear();

  // Make sure the main timeline is still paused after clearing it.
  if (isRoot) timeline.pause(0);
}

export const createDirectionalEase = (
  timelineRef: RefObject<gsap.core.Timeline | null>,
  forwardEaseName: string,
  reverseEaseName: string,
): gsap.EaseFunction => {
  const forwardEase = gsap.parseEase(forwardEaseName);
  const reverseEase = gsap.parseEase(reverseEaseName);

  return (progress: number): number => {
    if (timelineRef.current?.reversed()) {
      return 1 - reverseEase(1 - progress);
    }

    return forwardEase(progress);
  };
};
